import { useState, useEffect } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { routes } from '@/utils/routes'
import { touchPointsApi } from '@/api/touchPoints'
import type { TouchPointViewDto } from '@/types/touchPoint'

interface UseMeetingOverlayParams {
  /** The already-loaded meetings list (the store) for a store-first read. */
  touchPoints: TouchPointViewDto[]
  workspaceId: string
  campaignId: string
  companyId: string
  isCampaignLevel: boolean
  webId?: string
}

/**
 * URL-driven routed meeting overlay (ORB-436). The open meeting is derived from the URL — web scope uses a
 * path segment (/.../web/:webId/meeting/:id), campaign scope (query-based tabs) uses ?meeting=:id — so the
 * page is deep-linkable, refresh-safe, and Back-button closable. Reads the meeting from the already-loaded
 * list (store hit); on a cold load / deep link it fetches by id so the page never waits on the whole list.
 * Extracted from MeetingsTab to keep that component focused on the list UI.
 */
export function useMeetingOverlay({
  touchPoints,
  workspaceId,
  campaignId,
  companyId,
  isCampaignLevel,
  webId,
}: UseMeetingOverlayParams) {
  const location = useLocation()

  const overlayMeetingId = (() => {
    const path = (location as unknown as { path?: string })?.path
      || (typeof window !== 'undefined' ? window.location.pathname : '')
    const fromPath = /\/meeting\/([^/]+)\/?$/.exec(path)?.[1]
    if (fromPath) return fromPath
    try {
      const href = (location as unknown as { url?: string })?.url
        || (typeof window !== 'undefined' ? window.location.href : '')
      return new URL(href, 'http://localhost').searchParams.get('meeting')
    } catch {
      return null
    }
  })()

  const storeOverlayMeeting = touchPoints.find(tp => tp.Id === overlayMeetingId) ?? null

  // A store hit always wins and clears the fetched copy.
  const [fetchedOverlayMeeting, setFetchedOverlayMeeting] = useState<TouchPointViewDto | null>(null)
  useEffect(() => {
    if (!overlayMeetingId || storeOverlayMeeting) {
      setFetchedOverlayMeeting(null)
      return
    }
    let cancelled = false
    void touchPointsApi.getTouchPoint(workspaceId, overlayMeetingId).then(res => {
      if (!cancelled && res.data) setFetchedOverlayMeeting(res.data)
    })
    return () => { cancelled = true }
  }, [overlayMeetingId, storeOverlayMeeting, workspaceId])

  const overlayMeeting = storeOverlayMeeting ?? fetchedOverlayMeeting

  // Web scope routes to a path segment; campaign scope (query-based tabs) to ?tab=meetings&meeting=:id.
  // Closing drops back to the plain Meetings list URL.
  const openMeeting = (touchPointId: string) => {
    const scoped = routes.scoped(companyId, workspaceId)
    location.route(!isCampaignLevel && webId
      ? scoped.webMeeting(campaignId, webId, touchPointId)
      : scoped.campaignMeeting(campaignId, touchPointId))
  }
  const closeMeeting = () => {
    const scoped = routes.scoped(companyId, workspaceId)
    location.route(!isCampaignLevel && webId
      ? `${scoped.web(campaignId, webId)}/meetings`
      : `${scoped.campaign(campaignId)}?tab=meetings`)
  }

  return { overlayMeeting, openMeeting, closeMeeting }
}
